// src/components/housekeeping/HousekeepingManagement.js
import React, { useState, useEffect } from 'react';
import { supabase, supabaseAdmin } from '../../config/supabase.config';
import AttendanceMarking from './AttendanceMarking';
import TaskChecklist from './TaskChecklist';
import StaffManagement from './StaffManagement';
import HousekeepingReports from './HousekeepingReports';
import '../../pages/UserDashboard.css';
import './HousekeepingManagement.css';

function HousekeepingManagement({ userData, onBack }) {
  const [activeTab, setActiveTab] = useState('attendance');
  const [staff, setStaff] = useState([]);
  const [todayAttendance, setTodayAttendance] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(localStorage.getItem('darkMode') === 'true');
  
  const isAdmin = userData?.role === 'admin' || userData?.role === 'superadmin';

  useEffect(() => {
    fetchStaff();
    fetchTodayAttendance();
    if (darkMode) {
      document.body.classList.add('dark-mode');
    }
  }, [selectedDate, darkMode]);

  const fetchStaff = async () => {
    try {
      const { data, error } = await supabase
        .from('housekeeping_staff')
        .select('*')
        .eq('is_active', true)
        .order('name');

      if (!error) {
        setStaff(data || []);
      }
    } catch (error) {
      console.error('Error fetching staff:', error);
    }
  };

  const fetchTodayAttendance = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('housekeeping_attendance')
        .select('*')
        .eq('attendance_date', selectedDate);

      if (!error) {
        setTodayAttendance(data || []);
      }
    } catch (error) {
      console.error('Error fetching attendance:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTodayStats = () => {
    const present = todayAttendance.filter(a => a.status === 'present').length;
    const absent = staff.length - todayAttendance.length + 
                   todayAttendance.filter(a => a.status === 'absent').length;
    const halfDay = todayAttendance.filter(a => a.status === 'half_day').length;
    const leave = todayAttendance.filter(a => a.status === 'leave').length;

    return { present, absent, halfDay, leave };
  };

  const toggleDarkMode = () => {
    const newDarkMode = !darkMode;
    setDarkMode(newDarkMode);
    localStorage.setItem('darkMode', newDarkMode.toString());
    document.body.classList.toggle('dark-mode');
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  const stats = getTodayStats();

  return (
    <div className={`dashboard-container ${darkMode ? 'dark-mode' : ''}`}>
      <div 
        className={`mobile-overlay ${mobileMenuOpen ? 'active' : ''}`}
        onClick={closeMobileMenu}
      ></div>

      {/* Sidebar Navigation */}
      <aside className={`dashboard-sidebar ${mobileMenuOpen ? 'mobile-open' : ''}`}>
        <div className="sidebar-header">
          <div className="user-info">
            <div className="user-avatar">
              {userData?.name?.charAt(0).toUpperCase()}
            </div>
            <div className="user-details">
              <h3>{userData?.name}</h3>
              <p>Flat {userData?.flat_number}</p>
            </div>
          </div>
        </div>
        
        <nav className="sidebar-menu">
          <button
            className="menu-item"
            onClick={onBack}
          >
            <span className="menu-icon">🏠</span>
            <span className="menu-label">Dashboard</span>
          </button>
          
          <div className="sidebar-divider"></div>
          
          <button
            className={`menu-item ${activeTab === 'attendance' ? 'active' : ''}`}
            onClick={() => {
              setActiveTab('attendance');
              closeMobileMenu();
            }}
          >
            <span className="menu-icon">📋</span>
            <span className="menu-label">Attendance</span>
          </button>
          
          <button
            className={`menu-item ${activeTab === 'tasks' ? 'active' : ''}`}
            onClick={() => {
              setActiveTab('tasks');
              closeMobileMenu();
            }}
          >
            <span className="menu-icon">✅</span>
            <span className="menu-label">Tasks</span>
          </button>
          
          {isAdmin && (
            <>
              <button
                className={`menu-item ${activeTab === 'staff' ? 'active' : ''}`}
                onClick={() => {
                  setActiveTab('staff');
                  closeMobileMenu();
                }}
              >
                <span className="menu-icon">👥</span>
                <span className="menu-label">Staff</span>
              </button>
              
              <button
                className={`menu-item ${activeTab === 'reports' ? 'active' : ''}`}
                onClick={() => {
                  setActiveTab('reports');
                  closeMobileMenu();
                }}
              >
                <span className="menu-icon">📊</span>
                <span className="menu-label">Reports</span>
              </button>
            </>
          )}
        </nav>
        
        <div className="sidebar-footer">
          <button className="theme-toggle" onClick={toggleDarkMode}>
            <span className="menu-icon">{darkMode ? '☀️' : '🌙'}</span>
            <span className="menu-label">{darkMode ? 'Light Mode' : 'Dark Mode'}</span>
          </button>
        </div>
      </aside>
      
      {/* Main Content */}
      <main className="dashboard-main">
        {/* Mobile Header */}
        <header className="dashboard-header enhanced">
          <div className="header-left">
            <button 
              className={`mobile-menu-btn ${mobileMenuOpen ? 'active' : ''}`}
              onClick={toggleMobileMenu}
              aria-label="Toggle menu"
            >
              <span></span>
              <span></span>
              <span></span>
            </button>
            <h2 className="page-title-mobile">🧹 Housekeeping</h2>
          </div>
        </header>

        <div className="dashboard-content">
          {/* Desktop Header */}
          <div className="housekeeping-header">
            <h2 className="page-title">🧹 Housekeeping Management</h2>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              max={new Date().toISOString().split('T')[0]}
              className="date-picker"
            />
          </div>

          {/* Stats Cards */}
          <div className="dashboard-grid stats-grid">
            <div className="dashboard-card stat-card gradient-green">
              <div className="stat-content">
                <h3>✅ Present</h3>
                <p className="stat-value">{stats.present}</p>
                <small>Today</small>
              </div>
            </div>

            <div className="dashboard-card stat-card gradient-red">
              <div className="stat-content">
                <h3>❌ Absent</h3>
                <p className="stat-value">{stats.absent}</p>
                <small>Today</small>
              </div>
            </div>

            <div className="dashboard-card stat-card gradient-yellow">
              <div className="stat-content">
                <h3>⏰ Half Day</h3>
                <p className="stat-value">{stats.halfDay}</p>
                <small>Today</small>
              </div>
            </div>

            <div className="dashboard-card stat-card gradient-blue">
              <div className="stat-content">
                <h3>🏖️ Leave</h3>
                <p className="stat-value">{stats.leave}</p>
                <small>Today</small>
              </div>
            </div>

            <div className="dashboard-card stat-card gradient-purple">
              <div className="stat-content">
                <h3>👥 Total Staff</h3>
                <p className="stat-value">{staff.length}</p>
                <small>Active</small>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="housekeeping-tabs">
            <button
              onClick={() => setActiveTab('attendance')}
              className={`tab-button ${activeTab === 'attendance' ? 'active' : ''}`}
            >
              📋 <span className="tab-label">Attendance</span>
            </button>
            <button
              onClick={() => setActiveTab('tasks')}
              className={`tab-button ${activeTab === 'tasks' ? 'active' : ''}`}
            >
              ✅ <span className="tab-label">Tasks</span>
            </button>
            {isAdmin && (
              <>
                <button
                  onClick={() => setActiveTab('staff')}
                  className={`tab-button ${activeTab === 'staff' ? 'active' : ''}`}
                >
                  👥 <span className="tab-label">Staff</span>
                </button>
                <button
                  onClick={() => setActiveTab('reports')}
                  className={`tab-button ${activeTab === 'reports' ? 'active' : ''}`}
                >
                  📊 <span className="tab-label">Reports</span>
                </button>
              </>
            )}
          </div>

          {/* Tab Content */}
          <div className="dashboard-card housekeeping-content">
            {activeTab === 'attendance' && (
              <AttendanceMarking
                staff={staff}
                selectedDate={selectedDate}
                userData={userData}
                onUpdate={fetchTodayAttendance}
              />
            )}
            
            {activeTab === 'tasks' && (
              <TaskChecklist
                staff={staff}
                selectedDate={selectedDate}
                userData={userData}
              />
            )}
            
            {activeTab === 'staff' && isAdmin && (
              <StaffManagement
                staff={staff}
                onUpdate={fetchStaff}
              />
            )}
            
            {activeTab === 'reports' && isAdmin && (
              <HousekeepingReports
                staff={staff}
              />
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

export default HousekeepingManagement;